import fastdom from 'fastdom'
import type { ISiteScrollBlockerServiceConfig } from '@wix/viewer-service-site-scroll-blocker/definition'
import type { ISiteScrollBlocker } from '@wix/viewer-service-site-scroll-blocker/types'
import type { Experiments } from '@wix/thunderbolt-symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

type ISiteScrollBlockerServiceConfigThunderbolt = ISiteScrollBlockerServiceConfig & { experiments: Experiments }

const MENU_AS_CONTAINER_BLOCKER = 'MENU_AS_CONTAINER'

export const createSiteScrollBlocker = (
	{ onSiteScrollBlockChanged, shouldBlockScrollWithoutVar, experiments }: ISiteScrollBlockerServiceConfigThunderbolt = {
		experiments: {},
	}
): ISiteScrollBlocker => {
	const window = globalThis.window
	const isSSR = () => !window

	const shouldFixIosFlashBug = isExperimentOpen(experiments, 'specs.thunderbolt.shouldFixIosFlashBug')
	const shouldFixFirefoxPopupScrollShift = isExperimentOpen(experiments, 'specs.thunderbolt.fixFirefoxPopupScrollShift')
	const shouldReconcileBlockersOnDeferredWrites = isExperimentOpen(
		experiments,
		'specs.thunderbolt.fixSiteScrollBlockerRace'
	)

	let lastBlockListenerId = 0
	const blockListeners = new Map<unknown, any>()

	const blockSiteScrollingClassName = shouldBlockScrollWithoutVar ? 'siteScrollingBlocked' : 'blockSiteScrolling'
	let _activeBlockClassName: string = blockSiteScrollingClassName

	let blockers: Array<string> = []
	let _scrollCorrection = 0
	let siteContainerOriginMarginTop: string | undefined
	let _isIOSTouchScrollBlocked = false
	let _scrollbarWidth = 0
	let removePreventOutsideMenuTouchScrollListener: (() => void) | null = null

	let internalIsScrollBlocked = false

	const isScrollingBlocked = () => blockers.length > 0

	const restoreScrollPosition = () => {
		window!.scrollTo(0, _scrollCorrection)
	}

	const getSiteElements = () => ({
		bodyElement: document.body as HTMLBodyElement,
		siteContainerElement: document.getElementById('SITE_CONTAINER'),
		// dom-id-usage-check-ignore-next-line -- inlineContentParent-<compId> is a skin part inside the menu container, not a component root; it never carries an identity class
		menuInlineContentParentElement: document.getElementById('inlineContentParent-MENU_AS_CONTAINER'),
		popupsRootElement: document.getElementById('POPUPS_ROOT'),
	})

	const setMarginTop = (element: HTMLElement, marginTop: string = '') => {
		element.style && (element.style.marginTop = marginTop)
	}

	const setOverscrollBehaviorYContain = (element: HTMLElement | null) => {
		if (!element) {
			return
		}

		element.style.overscrollBehaviorY = 'contain'
	}

	const removeOverscrollBehaviorY = (element: HTMLElement | null) => {
		if (!element) {
			return
		}

		element.style.removeProperty('overscroll-behavior-y')
	}

	// Width the viewport scrollbar actually occupies in layout, measured while the site is still
	// scrollable. `body { overflow-y: scroll }` propagates to the viewport, so the scrollbar the
	// block is about to remove belongs to the viewport (which is also why the gutter goes on
	// <html>). Classic scrollbars take ~15px; overlay scrollbars (headless browsers, macOS
	// default, touch devices) take none — and then `overflow: hidden` reclaims nothing, so there
	// is nothing to reserve.
	const getViewportScrollbarWidth = () => {
		const { clientWidth } = document.documentElement
		// Environments without layout (e.g. jsdom) report 0 here, which would otherwise read as a
		// viewport-wide scrollbar. No layout means nothing to compensate for.
		if (!clientWidth) {
			return 0
		}

		return Math.max(window!.innerWidth - clientWidth, 0)
	}

	const setScrollbarGutterStable = () => {
		document.documentElement.style.setProperty('scrollbar-gutter', 'stable')
	}

	const removeScrollbarGutterStable = () => {
		document.documentElement.style.removeProperty('scrollbar-gutter')
	}

	const addPreventOutsideMenuTouchScrollListener = () => {
		if (removePreventOutsideMenuTouchScrollListener) {
			return
		}

		const preventOutsideMenuTouchScroll = (e: TouchEvent) => {
			const target = e.target as Element | null
			// dom-id-usage-check-ignore-next-line -- inlineContentParent-<compId> is a skin part inside the menu container, not a component root; it never carries an identity class
			const menuInlineContentParentElement = document.getElementById('inlineContentParent-MENU_AS_CONTAINER')
			const popupsRoot = document.getElementById('POPUPS_ROOT')

			const isInsideMenu = menuInlineContentParentElement?.contains(target)
			const isInsidePopup = popupsRoot?.contains(target)
			const shouldAllowScroll = isInsideMenu || isInsidePopup
			if (shouldAllowScroll) {
				return
			}

			e.preventDefault()
		}

		document.addEventListener('touchmove', preventOutsideMenuTouchScroll, { passive: false })

		removePreventOutsideMenuTouchScrollListener = () => {
			document.removeEventListener('touchmove', preventOutsideMenuTouchScroll)
			removePreventOutsideMenuTouchScrollListener = null
		}
	}

	const removePreventOutsideMenuTouchScrollListenerIfExists = () => {
		removePreventOutsideMenuTouchScrollListener?.()
	}

	const blockSiteScrolling = (blocker: string) => {
		const { bodyElement, siteContainerElement, menuInlineContentParentElement } = getSiteElements()

		fastdom.measure(() => {
			// The blockers may have been removed between scheduling this deferred measure and
			// it running (same-frame block/unblock churn). Re-derive from the source of truth so
			// we never re-block a site that no longer has any blockers — otherwise a stale
			// `internalIsScrollBlocked` can leave the block class on the body while `blockers` is empty.
			if (shouldReconcileBlockersOnDeferredWrites && blockers.length === 0) {
				return
			}

			const isAlreadyBlocked = internalIsScrollBlocked
			// The site should be blocked only when it's not already blocked
			if (!isAlreadyBlocked) {
				internalIsScrollBlocked = true
				_scrollCorrection = window!.scrollY
				// Read here, in the measure phase, while the site is still scrollable — by the time
				// the mutate below applies `overflow: hidden` the scrollbar is already gone.
				_scrollbarWidth = getViewportScrollbarWidth()
				fastdom.mutate(() => {
					// Reconcile again against the current blockers before touching the DOM.
					if (shouldReconcileBlockersOnDeferredWrites && blockers.length === 0) {
						internalIsScrollBlocked = false
						return
					}
					if (shouldBlockScrollWithoutVar) {
						const isMenuOpen = blockers.includes(MENU_AS_CONTAINER_BLOCKER)
						// Re-query POPUPS_ROOT here (not in getSiteElements at block entry): the popup root
						// mounts asynchronously and is only present by the time this fastdom mutate runs, so
						// an entry-time capture is null on Firefox and misses the popup. See TB-15655.
						const popupsRootElement = document.getElementById('POPUPS_ROOT')
						// Blocking popups with `position: fixed` (the else branch) collapses the document
						// scroll to 0 on Firefox, which desyncs scroll-scrubbed animations (e.g. strip Zoom)
						// and shifts them. The `overflow: hidden` iOS-fix path avoids that.
						const isPopupOpen = shouldFixFirefoxPopupScrollShift && !!popupsRootElement
						const shouldBlockIOSTouchScroll = shouldFixIosFlashBug && (isMenuOpen || isPopupOpen)
						if (shouldBlockIOSTouchScroll) {
							_isIOSTouchScrollBlocked = true
							_activeBlockClassName = 'siteScrollingBlockedIOSFix'
							// `overflow: hidden` takes the scrollbar away, reclaiming the width it held and
							// nudging content sideways; reserving a gutter gives that width back. The
							// gutter must go on <html>, not <body> — on <body> it has no effect.
							// Reserve it only when the scrollbar actually held width: with overlay
							// scrollbars nothing is reclaimed, so a gutter would *introduce* the very
							// nudge it exists to prevent.
							if (_scrollbarWidth > 0) {
								setScrollbarGutterStable()
							}
							setOverscrollBehaviorYContain(menuInlineContentParentElement)
							setOverscrollBehaviorYContain(popupsRootElement)
							addPreventOutsideMenuTouchScrollListener()
						} else {
							_activeBlockClassName = blockSiteScrollingClassName
							siteContainerOriginMarginTop = siteContainerElement?.style?.marginTop
							siteContainerElement && setMarginTop(siteContainerElement, `calc(${_scrollCorrection}px*-1)`)
						}
					} else {
						_activeBlockClassName = blockSiteScrollingClassName
						bodyElement.style.setProperty('--blocked-site-scroll-margin-top', `${Math.max(0.5, _scrollCorrection)}px`)
						removePreventOutsideMenuTouchScrollListenerIfExists()
					}

					bodyElement.classList.add(_activeBlockClassName)
				})
			}
		})

		blockListeners.forEach(({ handleBlockedBy }) => handleBlockedBy && handleBlockedBy(blocker))
	}

	const unblockSiteScrolling = (blocker: string) => {
		const { bodyElement, siteContainerElement, menuInlineContentParentElement, popupsRootElement } = getSiteElements()

		internalIsScrollBlocked = false
		fastdom.mutate(() => {
			bodyElement.classList.remove(_activeBlockClassName)

			if (shouldBlockScrollWithoutVar) {
				if (_isIOSTouchScrollBlocked) {
					_isIOSTouchScrollBlocked = false
					removeScrollbarGutterStable()
					removeOverscrollBehaviorY(menuInlineContentParentElement)
					removeOverscrollBehaviorY(popupsRootElement)
					removePreventOutsideMenuTouchScrollListenerIfExists()
				} else {
					siteContainerElement && setMarginTop(siteContainerElement, siteContainerOriginMarginTop)
				}
			} else {
				bodyElement.style.removeProperty('--blocked-site-scroll-margin-top')
				removePreventOutsideMenuTouchScrollListenerIfExists()
			}

			restoreScrollPosition()
		})

		blockListeners.forEach(({ handleUnblockedBy }) => handleUnblockedBy && handleUnblockedBy(blocker))
	}

	const addBlocker = (blocker: string) => {
		blockers = !blockers.includes(blocker) ? [...blockers, blocker] : blockers
		onSiteScrollBlockChanged && onSiteScrollBlockChanged(isScrollingBlocked())

		// The site should be blocked only when there's one blocker,
		// otherwise it's already blocked (more than one) or doesn't need to be blocked (zero)
		const shouldBlock = blockers.length === 1

		if (shouldBlock) {
			blockSiteScrolling(blocker)
		}
	}

	const removeBlocker = (blocker: string) => {
		const [activeBlocker] = blockers
		blockers = blockers.filter((b) => b !== blocker)
		const [newActiveBlocker] = blockers

		onSiteScrollBlockChanged && onSiteScrollBlockChanged(isScrollingBlocked())

		// The active blocker changes if we remove the blockers not from the end to start.
		// For example, removing from start to end, the active blocker should be adjusted each time (because the first blocker changes)
		const hasActiveBlockerChanged = activeBlocker !== newActiveBlocker

		if (hasActiveBlockerChanged) {
			if (newActiveBlocker) {
				blockSiteScrolling(blocker)
			} else {
				unblockSiteScrolling(blocker)
			}
		}
	}

	const setSiteScrollingBlocked = (blocked: boolean, compId: string) => {
		if (isSSR()) {
			return
		}

		return blocked ? addBlocker(compId) : removeBlocker(compId)
	}

	return {
		setSiteScrollingBlocked,
		registerScrollBlockedListener(listener) {
			const listenerId = ++lastBlockListenerId
			blockListeners.set(listenerId, listener)
			return listenerId
		},
		unRegisterScrollBlockedListener(listenerId) {
			blockListeners.delete(listenerId)
		},
		isScrollingBlocked,
	}
}
